import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import {
  MaterialPaginatedResponse,
  MaterialRequest,
  MaterialResponse,
  MaterialType,
  MaterialWithSuppliersPaginatedResponse,
} from "../types";
import { queryKeys } from "./queryKeys";
import { AxiosError } from "axios";

export type MaterialOpportunitiesByLastIdType = {
  countryIds: string[];
  page: number;
  type: MaterialType;
  categoryIds: string[];
};

export type SearchMaterialOpportunitiesByLastIdType = {
  query: string;
  page: number;
  type: MaterialType;
  categoryIds: string[];
  countryIds: string[];
};

const onGetAllCompanyMaterials = async (
  type: MaterialType = MaterialType.ALL,
  page = 0,
  tagIds: string[] = [],
  size = 10
) => {
  const params = new URLSearchParams();
  tagIds.forEach((id) => params.append("tagIds", id));
  params.append("size", String(size));
  const queryString = params.toString();
  const { data } = await axios.get(
    `/material/company/name/${type}/${page}${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

// `Page<MaterialCompany>` on the wire (MaterialController#getMaterialByCategoryNameAndCompany) —
// same shape `useMaterials`/`useAllMaterials` below carry (nested `material` plus the three
// supplier/order counters), not the flat `MaterialResponse` the search sibling returns.
export const useAllCompanyMaterials = (
  type: MaterialType = MaterialType.ALL,
  page: number,
  tagIds: string[] = [],
  size = 10
) =>
  useQuery<MaterialWithSuppliersPaginatedResponse>({
    queryKey: [queryKeys.ALL_COMPANY_MATERIALS, type, page, tagIds, size],
    queryFn: () => onGetAllCompanyMaterials(type, page, tagIds, size),
    placeholderData: (previous) => previous,
  });

const onGetMaterial = async (materialId: string) => {
  const { data } = await axios.get(`/material/${materialId}`);
  return data;
};

export const useMaterial = (materialId: string) =>
  useQuery<MaterialResponse>({
    queryKey: [queryKeys.MATERIAL, materialId],
    queryFn: () => onGetMaterial(materialId),
    enabled: !!materialId,
    retry: 1,
  });

const onToggleMaterialStatus = async (materialId: string) => {
  const { data } = await axios.get(`/material/toggleActivate/${materialId}`);
  return data;
};

export const useToggleMaterialStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onToggleMaterialStatus,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeys.MATERIAL] }),
  });
};

const onSearchMaterials = async (
  query: string,
  page = 0,
  tagIds: string[] = [],
  companyIds: string[] = [],
  ownerIds: string[] = [],
  size = 10
) => {
  const params = new URLSearchParams();
  tagIds.forEach((id) => params.append("tagIds", id));
  companyIds.forEach((id) => params.append("companyIds", id));
  ownerIds.forEach((id) => params.append("ownerIds", id));
  params.append("size", String(size));
  const queryString = params.toString();
  const { data } = await axios.get(
    `/material/search/${query}/${page}${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

// `Page<Material>` on the wire (MaterialController#getMaterialByQuery) — the FLAT
// `MaterialResponse` shape, unlike the list endpoints above: no `suppliersQuantity`/
// `supplierValidatedQuantity`/`activeOrdersQuantity`, no nested `material` wrapper.
export const useSearchMaterials = (
  searchTerm: string,
  page: number,
  tagIds: string[] = [],
  companyIds: string[] = [],
  ownerIds: string[] = [],
  size = 10
) =>
  useQuery<MaterialPaginatedResponse>({
    queryKey: [
      queryKeys.FOUND_MATERIALS,
      searchTerm,
      page,
      tagIds,
      companyIds,
      ownerIds,
      size,
    ],
    queryFn: () =>
      onSearchMaterials(searchTerm, page, tagIds, companyIds, ownerIds, size),
    enabled: !!searchTerm,
    placeholderData: (previous) => previous,
  });

const onSearchAllMaterials = async (
  name: MaterialType,
  query: string,
  page = 0,
  tagIds: string[] = [],
  size = 10
) => {
  const params = new URLSearchParams();
  tagIds.forEach((id) => params.append("tagIds", id));
  params.append("size", String(size));
  const queryString = params.toString();
  const { data } = await axios.get(
    `/material/company/search/${name}/${query}/${page}${
      queryString ? `?${queryString}` : ""
    }`
  );
  return data;
};

// `Page<Material>` on the wire (MaterialController#getMaterialCompanyByQuery) — same flat
// `MaterialResponse` shape `useSearchMaterials` above documents, no supplier/order counters.
export const useSearchAllMaterials = (
  category: MaterialType = MaterialType.DIRECT_GOODS,
  debouncedSearchTerm: string,
  page: number,
  tagIds: string[] = [],
  size = 10
) =>
  useQuery<MaterialPaginatedResponse>({
    // `category` added to the key (Task 5): it was missing before, so switching the material-type
    // filter while a search term was active reused the previous type's cached page instead of
    // refetching — this DataTable rewire is what surfaced it.
    queryKey: [
      queryKeys.FOUND_MATERIALS,
      category,
      page,
      debouncedSearchTerm,
      tagIds,
      size,
    ],
    queryFn: () =>
      onSearchAllMaterials(category, debouncedSearchTerm, page, tagIds, size),
    enabled: !!debouncedSearchTerm,
    placeholderData: (previous) => previous,
  });

const onCreateMaterial = async (body: MaterialRequest) => {
  const { data } = await axios.post(`/material?async=${body.async}`, body);
  return data;
};

export const useCreateMaterial = (
  onSuccess: (material: MaterialResponse) => void,
  onError: (error: AxiosError) => void
) =>
  useMutation({
    mutationFn: onCreateMaterial,
    onSuccess,
    onError,
  });

const onEditMaterial = async ({
  materialId,
  body,
}: {
  materialId: string;
  body: any;
}) => {
  const { data } = await axios.put(`/material/${materialId}`, body);
  return data;
};

export const useUpdateMaterial = (
  onSuccess: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onEditMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.MATERIAL] });
      return onSuccess();
    },
    onError,
  });
};

const onGetMaterialsByOwner = async (
  type: MaterialType = MaterialType.DIRECT_GOODS,
  page = 0,
  tagIds: string[] = []
): Promise<MaterialWithSuppliersPaginatedResponse> => {
  const params = new URLSearchParams();
  tagIds.forEach((id) => params.append("tagIds", id));
  const queryString = params.toString();
  const { data } = await axios.get(
    `/material/list/user/type/${type}/${page}${
      queryString ? `?${queryString}` : ""
    }`
  );
  return data;
};

export const useMaterialsByOwner = (
  type: MaterialType = MaterialType.DIRECT_GOODS,
  page: number,
  tagIds: string[] = []
) =>
  useQuery({
    queryKey: [queryKeys.MATERIALS_BY_OWNER, type, page, tagIds],
    queryFn: () => onGetMaterialsByOwner(type, page, tagIds),
  });

const onDelegateAllMaterials = async (userId: string) => {
  const { data } = await axios.put(`/material/delegate/list/${userId}`);
  return data;
};

export const useDelegateAllMaterials = (
  onSuccess: () => void,
  onError: () => void
) =>
  useMutation({
    mutationFn: onDelegateAllMaterials,
    onSuccess,
    onError,
  });

const onGetBackAllMaterials = async () => {
  const { data } = await axios.put(`/material/delegate/list`);
  return data;
};

export const useRetrieveAllMaterials = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onGetBackAllMaterials,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MATERIALS_BY_OWNER],
      });
      return onSuccess();
    },
    onError,
  });
};

const onGetOpportunitiesByLastIds = async ({
  countryIds,
  page = 0,
  type = MaterialType.DIRECT_GOODS,
  categoryIds,
}: MaterialOpportunitiesByLastIdType) => {
  const { data } = await axios.post(
    `/material/categories/search/${page}/${type}/last?countryIds=${countryIds.join(
      ","
    )}`,
    categoryIds
  );
  return data;
};

export const useOpportunitiesByLastId = (
  onSuccess?: (res: any) => void,
  onError?: () => void
) =>
  useMutation({
    mutationFn: onGetOpportunitiesByLastIds,
    onSuccess,
    onError,
  });

const onSearchOpportunitiesByLastIds = async ({
  query,
  page = 0,
  type,
  categoryIds,
  countryIds,
}: SearchMaterialOpportunitiesByLastIdType) => {
  const { data } = await axios.post(
    `/material/categories/search/${page}/${type}/last?query=${query}&countryIds=${countryIds.join(
      ","
    )}`,
    categoryIds
  );
  return data;
};

export const useSearchOpportunitiesByLastIds = () =>
  useMutation({
    mutationFn: onSearchOpportunitiesByLastIds,
  });

const onGetMaterials = async (
  type: MaterialType = MaterialType.DIRECT_GOODS,
  page = 0,
  tagIds: string[] = [],
  companyIds: string[] = [],
  ownerIds: string[] = [],
  size = 10
): Promise<MaterialWithSuppliersPaginatedResponse> => {
  const params = new URLSearchParams();
  tagIds.forEach((id) => params.append("tagIds", id));
  companyIds.forEach((id) => params.append("companyIds", id));
  ownerIds.forEach((id) => params.append("ownerIds", id));
  params.append("size", String(size));
  const queryString = params.toString();
  const { data } = await axios.get(
    `/material/list/type/${type}/${page}${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

export const useMaterials = (
  queryKey: string,
  type: MaterialType = MaterialType.DIRECT_GOODS,
  page: number,
  tagIds: string[] = [],
  companyIds: string[] = [],
  ownerIds: string[] = [],
  size = 10
) =>
  useQuery<MaterialWithSuppliersPaginatedResponse>({
    queryKey: [queryKey, type, page, tagIds, companyIds, ownerIds, size],
    queryFn: () =>
      onGetMaterials(type, page, tagIds, companyIds, ownerIds, size),
    enabled: type !== MaterialType.ALL,
    placeholderData: (previous) => previous,
  });

const onGetAllMaterials = async (
  page = 0,
  tagIds: string[] = [],
  companyIds: string[] = [],
  ownerIds: string[] = [],
  size = 10
) => {
  const params = new URLSearchParams();
  tagIds.forEach((id) => params.append("tagIds", id));
  companyIds.forEach((id) => params.append("companyIds", id));
  ownerIds.forEach((id) => params.append("ownerIds", id));
  params.append("size", String(size));
  const queryString = params.toString();
  const { data } = await axios.get(
    `/material/list/${page}${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

export const useAllMaterials = (
  materialType: MaterialType,
  page: number,
  tagIds: string[] = [],
  companyIds: string[] = [],
  ownerIds: string[] = [],
  size = 10
) =>
  useQuery<MaterialWithSuppliersPaginatedResponse>({
    queryKey: [
      queryKeys.ALL_MATERIALS,
      page,
      tagIds,
      companyIds,
      ownerIds,
      size,
    ],
    queryFn: () => onGetAllMaterials(page, tagIds, companyIds, ownerIds, size),
    enabled: materialType === MaterialType.ALL,
    placeholderData: (previous) => previous,
  });
